import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GB, s_GC, s_PL } from '../../../shared/styles';
import { CLAUDE_HEADERS } from '../../../shared/api';
import { trackSocialEvent } from '../../../shared/dbService';
import Avatar from '../../../components/ui/Avatar';

// ─── Breath phase labels ───────────────────────────────────────────────────
const BREATH_LABELS = {
  in:      { nl: "Adem in",   en: "Breathe in"  },
  holdIn:  { nl: "Vasthouden",en: "Hold"         },
  out:     { nl: "Adem uit",  en: "Breathe out"  },
  holdOut: { nl: "Pauzeer",   en: "Pause"        },
};

function BreathingCircle({ exercise, breathState, nl }) {
  const { phase, progress, secs } = breathState;

  // Scale: in → grow 0.45→1.0, holdIn → 1.0, out → shrink 1.0→0.45, holdOut → 0.45
  const scale =
    phase === 'in'      ? 0.45 + 0.55 * progress :
    phase === 'holdIn'  ? 1.0 :
    phase === 'out'     ? 1.0 - 0.55 * progress :
    0.45;

  const label = BREATH_LABELS[phase] || BREATH_LABELS.in;
  const BASE_R = 80;
  const r = BASE_R * scale;

  // Glow pulsing with scale
  const glowSize = Math.round(scale * 40);

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      margin: "8px 0 20px",
      position: "relative",
    }}>
      {/* Outer ambient ring */}
      <div style={{
        position: "absolute",
        width: BASE_R * 2 + 60, height: BASE_R * 2 + 60,
        borderRadius: "50%",
        border: `1px solid ${exercise.color}18`,
        pointerEvents: "none",
      }}/>
      <div style={{
        position: "absolute",
        width: BASE_R * 2 + 30, height: BASE_R * 2 + 30,
        borderRadius: "50%",
        border: `1px solid ${exercise.color}10`,
        pointerEvents: "none",
      }}/>

      {/* Breathing circle */}
      <div style={{
        width: BASE_R * 2 + 60, height: BASE_R * 2 + 60,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{
          width: r * 2, height: r * 2,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${exercise.color}30 0%, ${exercise.color}08 70%)`,
          border: `2px solid ${exercise.color}${Math.round(scale * 99).toString(16).padStart(2,'0')}`,
          boxShadow: `0 0 ${glowSize}px ${exercise.color}40, 0 0 ${glowSize * 2}px ${exercise.color}15`,
          transition: "width 0.15s ease-out, height 0.15s ease-out, box-shadow 0.15s ease",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
        }}>
          {/* Phase label */}
          <div style={{
            color: exercise.color,
            fontSize: Math.max(11, Math.round(scale * 15)),
            fontWeight: 700,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            transition: "font-size 0.15s ease",
            textAlign: "center",
            lineHeight: 1.2,
            padding: "0 8px",
          }}>
            {nl ? label.nl : label.en}
          </div>
          {/* Countdown */}
          {secs > 0 && (
            <div style={{
              color: C.white,
              fontSize: Math.max(18, Math.round(scale * 28)),
              fontWeight: 700,
              fontFamily: "Inter, sans-serif",
              marginTop: 4,
              transition: "font-size 0.15s ease",
            }}>
              {secs}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// TTS available to all paid tiers (not freemium)
const TTS_LICENSES = ['starter', 'athlete', 'sensei', 'professional', 'individual', 'companies', 'coaches_solo', 'coaches_club', 'coaches_pro', 'coaches_elite'];

function ExerciseSession({exercise, lang, preData, partner, license, onFinish}) {
  const nl = lang === "nl";
  const totalSecs = exercise.duration * 60;
  const [secondsLeft, setSecondsLeft] = useState(totalSecs);
  const [running, setRunning] = useState(true);
  const [done, setDone] = useState(false);
  const [selFeelings, setSelFeelings] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiComment, setAiComment] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const ttsEnabledRef = useRef(false);
  const canUseTts = TTS_LICENSES.includes(license) && 'speechSynthesis' in window;
  // Auto-enable for paid users that have a sound theme active
  const [ttsEnabled, setTtsEnabled] = useState(() => {
    if (!TTS_LICENSES.includes(license) || !('speechSynthesis' in window)) return false;
    const theme = localStorage.getItem('sl_sound_theme');
    return !!theme && theme !== 'off';
  });

  // Breathing animation state
  const [breathState, setBreathState] = useState({ phase: 'in', progress: 0, secs: exercise.breathCycle?.in || 4 });
  const breathRef = useRef({ cycleMs: 0 });
  const timerRef = useRef(null);
  const breathTimerRef = useRef(null);

  const elapsed = totalSecs - secondsLeft;

  // Determine current instruction step
  const currentStep = (() => {
    if (!exercise.steps || exercise.steps.length === 0) return null;
    let step = exercise.steps[0];
    for (const s of exercise.steps) {
      if (elapsed >= s.at) step = s;
      else break;
    }
    return step;
  })();

  // Sync TTS enabled ref (so effects always see the current value)
  useEffect(() => { ttsEnabledRef.current = ttsEnabled; }, [ttsEnabled]);

  // Speak current instruction when step changes
  useEffect(() => {
    if (!ttsEnabledRef.current || !currentStep || !running || done) return;
    const text = nl ? currentStep.text : currentStep.textEN;
    if (!text) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = nl ? 'nl-NL' : 'en-US';
    utt.rate = 0.9;
    window.speechSynthesis.speak(utt);
  }, [currentStep?.at]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cancel TTS when session ends
  useEffect(() => {
    if (done) window.speechSynthesis?.cancel();
  }, [done]);

  // Main countdown timer
  useEffect(() => {
    if (!running || done) return;
    timerRef.current = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) { clearInterval(timerRef.current); setDone(true); setRunning(false); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running, done]);

  // Breathing animation timer — runs at 100ms for smooth animation
  useEffect(() => {
    const bc = exercise.breathCycle;
    if (!bc || !running || done) return;

    const phases = [
      { name: 'in',      dur: bc.in },
      { name: 'holdIn',  dur: bc.holdIn  || 0 },
      { name: 'out',     dur: bc.out },
      { name: 'holdOut', dur: bc.holdOut || 0 },
    ].filter(p => p.dur > 0);

    const totalCycleMs = phases.reduce((s, p) => s + p.dur * 1000, 0);

    breathTimerRef.current = setInterval(() => {
      breathRef.current.cycleMs = (breathRef.current.cycleMs + 100) % totalCycleMs;
      let rem = breathRef.current.cycleMs / 1000;

      let currentPhase = phases[0];
      for (const p of phases) {
        if (rem < p.dur) { currentPhase = p; break; }
        rem -= p.dur;
      }
      const progressWithinPhase = Math.min(rem / currentPhase.dur, 1);
      const secsLeft = Math.max(1, Math.ceil(currentPhase.dur - rem));

      setBreathState({ phase: currentPhase.name, progress: progressWithinPhase, secs: secsLeft });
    }, 100);

    return () => clearInterval(breathTimerRef.current);
  }, [exercise.breathCycle, running, done]);

  const getAiComment = async (feelings) => {
    setAiLoading(true);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:CLAUDE_HEADERS,body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:120,system:"Warm wellness coach. Give a 1-2 sentence encouraging comment on completing an exercise session. Be specific and warm. Max 40 words.",messages:[{role:"user",content:`Exercise: ${exercise.labelEN}. Pre-session energy: ${preData?.energy}/5. Intention: ${preData?.focus}. Post feelings: ${feelings.join(', ')}. Give a short warm comment.`}]})});
      const data = await res.json();
      setAiComment(data.content?.[0]?.text || "Great work completing your session!");
    } catch { setAiComment("Wonderful effort! Every session counts towards your growth. 🌱"); }
    setAiLoading(false);
  };

  const finishSession = () => {
    if (partner) {
      trackSocialEvent('exercise_partner', { targetId: partner.id, weight: 5 });
      onFinish({feelings:selFeelings, aiComment, partnerScore: Math.floor(Math.random()*30)+60, myScore: 85});
    } else {
      onFinish({feelings:selFeelings, aiComment});
    }
  };

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const progress = elapsed / totalSecs;
  const r = 38, circ = 2 * Math.PI * r;
  const offset = circ * (1 - progress);
  const feelings = nl ? exercise.choices : exercise.choicesEN;
  const hasBreathAnim = !!exercise.breathCycle;

  // ─── Session complete screen ──────────────────────────────────────────────
  if (done && !showResults) {
    return (
      <div className="fadeUp" style={{textAlign:"center",padding:"10px 0"}}>
        <div style={{fontSize:56,marginBottom:12}}>✅</div>
        <div style={{fontFamily:"Georgia,serif",fontSize:22,color:C.white,marginBottom:6}}>
          {nl?"Sessie voltooid!":"Session complete!"}
        </div>
        {preData?.focus && (
          <div style={{color:C.silver,fontSize:13,marginBottom:16}}>
            {nl?"Doel":"Goal"}: <span style={{color:_T.accent}}>{preData.focus}</span>
          </div>
        )}
        <div style={{color:C.silver,fontSize:13,marginBottom:16,lineHeight:1.6}}>
          {nl?"Hoe voel je je nu?":"How do you feel now?"}
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:8,justifyContent:"center",marginBottom:20}}>
          {feelings.map(f=>(
            <button key={f} onClick={()=>setSelFeelings(p=>p.includes(f)?p.filter(x=>x!==f):[...p,f])} style={s_PL(selFeelings.includes(f),exercise.color)}>{f}</button>
          ))}
        </div>
        {selFeelings.length>0 && !aiComment && !aiLoading && (
          <button style={s_GB({width:"100%",marginBottom:10,fontSize:13})} onClick={()=>getAiComment(selFeelings)}>
            🦋 {nl?"Vraag coach feedback":"Get coach feedback"}
          </button>
        )}
        {aiLoading && <div style={{color:C.silver,fontSize:13,marginBottom:10}}>🦋 ···</div>}
        {aiComment && (
          <div style={{...s_GC({padding:"12px 16px",marginBottom:16,textAlign:"left"})}}>
            <div style={{fontSize:9,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:4}}>🦋 COACH</div>
            <div style={{color:C.silver2,fontSize:13,lineHeight:1.6,fontStyle:"italic"}}>"{aiComment}"</div>
          </div>
        )}
        <button style={s_PB({width:"100%",fontSize:15})} onClick={()=>{setShowResults(true);finishSession();}}>
          {nl?"Afronden →":"Finish →"}
        </button>
      </div>
    );
  }

  // ─── Active session ───────────────────────────────────────────────────────
  return (
    <div className="fadeUp" style={{textAlign:"center"}}>

      {/* Goal card */}
      {preData?.focus && (
        <div style={{...s_GC({padding:"8px 14px",marginBottom:10,textAlign:"left"}),display:"flex",alignItems:"center",gap:8}}>
          <div style={{fontSize:9,color:_T.accent,letterSpacing:3,fontWeight:600,whiteSpace:"nowrap"}}>
            {nl?"DOEL":"GOAL"}
          </div>
          <div style={{color:C.silver2,fontSize:12}}>{preData.focus}</div>
        </div>
      )}

      {/* Partner indicator */}
      {partner && (
        <div style={{...s_GC({padding:"8px 14px",marginBottom:10}),display:"flex",alignItems:"center",gap:10}}>
          <Avatar user={partner} size={28}/>
          <div style={{flex:1,textAlign:"left"}}>
            <div style={{color:C.white,fontSize:12,fontWeight:600}}>{partner.name}</div>
            <div style={{fontSize:12,color:C.teal}}>🟢 {nl?"Ook aan het oefenen":"Also exercising"}</div>
          </div>
        </div>
      )}

      {/* ── Breathing animation (for breath exercises) ── */}
      {hasBreathAnim ? (
        <>
          <BreathingCircle exercise={exercise} breathState={breathState} nl={nl} />

          {/* Small compact timer */}
          <div style={{
            display:"inline-flex",alignItems:"center",gap:10,
            background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,
            borderRadius:24,padding:"6px 16px",marginBottom:18,
          }}>
            <svg width={22} height={22}>
              <circle cx={11} cy={11} r={9} fill="none" stroke={_T.bg4} strokeWidth={3}/>
              <circle cx={11} cy={11} r={9} fill="none" stroke={exercise.color} strokeWidth={3}
                strokeDasharray={2*Math.PI*9} strokeDashoffset={2*Math.PI*9*(1-progress)}
                strokeLinecap="round" transform="rotate(-90 11 11)"
                style={{transition:"stroke-dashoffset 1s linear"}}/>
            </svg>
            <span style={{fontFamily:"Inter",fontSize:17,fontWeight:700,color:C.white,letterSpacing:2}}>
              {String(mins).padStart(2,'0')}:{String(secs).padStart(2,'0')}
            </span>
            <span style={{fontSize:11,color:C.silver}}>{nl?"over":"left"}</span>
          </div>
        </>
      ) : (
        /* ── Standard timer ring (non-breath exercises) ── */
        <div style={{position:"relative",display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:18,marginTop:6}}>
          <svg width={110} height={110}>
            <circle cx={55} cy={55} r={r} fill="none" stroke={_T.bg4} strokeWidth={7}/>
            <circle cx={55} cy={55} r={r} fill="none" stroke={exercise.color} strokeWidth={7}
              strokeDasharray={circ} strokeDashoffset={offset}
              strokeLinecap="round" transform="rotate(-90 55 55)"
              style={{transition:"stroke-dashoffset 1s linear"}}/>
            <text x={55} y={50} textAnchor="middle" fill={C.white} fontSize={20} fontWeight="700" fontFamily="Inter">
              {String(mins).padStart(2,'0')}:{String(secs).padStart(2,'0')}
            </text>
            <text x={55} y={66} textAnchor="middle" fill={C.silver} fontSize={10} fontFamily="Inter">
              {nl?"resterend":"remaining"}
            </text>
          </svg>
        </div>
      )}

      {/* ── Live instruction card ── */}
      {currentStep && (
        <div
          key={currentStep.at}
          className="fadeUp"
          style={{
            ...s_GC({padding:"16px 18px",marginBottom:16,textAlign:"left"}),
            borderLeft:`3px solid ${exercise.color}`,
          }}
        >
          <div style={{fontSize:9,color:exercise.color,letterSpacing:3,fontWeight:600,marginBottom:8}}>
            {nl?"INSTRUCTIE":"INSTRUCTION"}
          </div>
          <div style={{color:C.white,fontSize:15,lineHeight:1.75,fontWeight:400,fontFamily:"Georgia,serif"}}>
            {nl ? currentStep.text : currentStep.textEN}
          </div>
        </div>
      )}

      {/* ── Controls ── */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <button style={s_GB({fontSize:14,padding:"12px"})} onClick={()=>setRunning(r=>!r)}>
          {running?(nl?"⏸ Pauzeer":"⏸ Pause"):(nl?"▶ Hervat":"▶ Resume")}
        </button>
        <button
          style={{...s_GB({fontSize:14,padding:"12px"}),borderColor:`${C.red}44`,color:C.red}}
          onClick={()=>{clearInterval(timerRef.current);clearInterval(breathTimerRef.current);setDone(true);}}>
          {nl?"⏹ Stop":"⏹ Stop"}
        </button>
      </div>

      {/* ── TTS toggle (sensei/professional only) ── */}
      {canUseTts ? (
        <button
          style={{...s_GB({fontSize:12,padding:"9px",marginTop:8,width:"100%"}),
            background: ttsEnabled ? `${exercise.color}18` : undefined,
            borderColor: ttsEnabled ? `${exercise.color}66` : undefined,
            color: ttsEnabled ? exercise.color : C.silver,
          }}
          onClick={() => {
            const next = !ttsEnabled;
            setTtsEnabled(next);
            ttsEnabledRef.current = next;
            if (!next) {
              window.speechSynthesis.cancel();
            } else if (currentStep) {
              // Speak synchronously inside the user gesture so the browser unlocks
              // speech synthesis for all subsequent effect-triggered calls.
              const text = nl ? currentStep.text : currentStep.textEN;
              if (text) {
                window.speechSynthesis.cancel();
                const utt = new SpeechSynthesisUtterance(text);
                utt.lang = nl ? 'nl-NL' : 'en-US';
                utt.rate = 0.9;
                window.speechSynthesis.speak(utt);
              }
            }
          }}>
          🔊 {ttsEnabled ? (nl?"Stem: Aan":"Voice: On") : (nl?"Stem: Uit":"Voice: Off")}
        </button>
      ) : license && !TTS_LICENSES.includes(license) ? (
        <div style={{fontSize:11,color:C.silver,textAlign:"center",marginTop:8,opacity:0.6}}>
          🔊 {nl?"Stemgeleiding beschikbaar voor betaalde gebruikers":"Voice guidance available for paid users"}
        </div>
      ) : null}
    </div>
  );
}

export default ExerciseSession;
